import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCustomer } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import { showNotification } from '@/lib/generalNotifications';
import { Timestamp } from 'firebase/firestore';
import type { Customer } from '@/types/firebase';
import { CustomerFormFields, type CustomerFormData } from '@/components/forms/CustomerFormFields';

interface AddCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCustomerCreated?: (customer: Customer) => void;
}

interface ExtendedCustomerFormData extends CustomerFormData {
  /** @deprecated Field kept for compatibility with existing data */
  type?: 'Owner' | 'Renter';
  /** @deprecated Field kept for compatibility with existing data */
  numberOfGuests?: number;
}

export function AddCustomerDialog({ open, onOpenChange, onCustomerCreated }: AddCustomerDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [customerFormData, setCustomerFormData] = useState<ExtendedCustomerFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    street: '',
    city: '',
    state: '',
    country: 'United States',
    zip: '',
    notes: '',
    cabinNumber: '',
    hostName: '',
    type: 'Owner', // For backwards compatibility
    numberOfGuests: 1, // For backwards compatibility
  });

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (customer: Omit<Customer, 'id' | 'createdAt'>) => 
      createCustomer(customer, user?.companyId || ''),
    onSuccess: (newCustomer: Customer) => {
      // Reset form
      setCustomerFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        street: '',
        city: '',
        state: '',
        country: 'United States',
        zip: '',
        notes: '',
        cabinNumber: '',
        hostName: '',
        type: 'Owner',
        numberOfGuests: 1,
      });
      // Close dialog
      onOpenChange(false);
      // Call the callback with the new customer
      onCustomerCreated?.(newCustomer);
    }
  });

  const handleFieldChange = (field: keyof CustomerFormData, value: string) => {
    setCustomerFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (address: { street: string; city: string; state: string; zipCode: string }) => {
    setCustomerFormData(prev => ({
      ...prev,
      street: address.street,
      city: address.city,
      state: address.state,
      zip: address.zipCode
    }));
  };

  const handleCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!user) {
        throw new Error('You must be logged in to create a customer');
      }

      await createCustomerMutation.mutateAsync({
        ...customerFormData,
        // Ensure these fields have default values for backward compatibility
        type: customerFormData.type || 'Owner',
        cabinNumber: customerFormData.cabinNumber || 'N/A',
        numberOfGuests: customerFormData.numberOfGuests || 0,
        status: 'active',
        paymentStatus: 'pending',
        companyId: user.companyId,
        // Explicitly add fields required by the firestore rules
        updatedAt: Timestamp.now()
        // The serverTimestamp() will be added by the API function
      });
      
      // After success, fetch the updated customer list
      queryClient.invalidateQueries({ queryKey: ['customers'], exact: false });
      
      // Show success message
      showNotification({
        title: "Success",
        message: "Customer added successfully",
        type: "success"
      });
    } catch (error) {
      console.error('Error creating customer:', error);
      showNotification({
        title: "Error",
        message: `Failed to create customer: ${error instanceof Error ? error.message : String(error)}`,
        type: "error"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCustomerSubmit} className="space-y-6">
          <CustomerFormFields
            formData={customerFormData}
            onChange={handleFieldChange}
            onAddressChange={handleAddressChange}
            showCruiseFields={false}
          />
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createCustomerMutation.isPending}>
              {createCustomerMutation.isPending ? 'Adding...' : 'Add Customer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
